import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress, Chip,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper
} from '@mui/material';
import { Refresh as RefreshIcon, Edit as EditIcon, Cable as CableIcon } from '@mui/icons-material';
import api from '../../config/api';
import CompanyModulesDialog from './CompanyModulesDialog';
import CompanyConnectorsDialog from './CompanyConnectorsDialog';

const CompanyModulesTab = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [editingConnectors, setEditingConnectors] = useState(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/users/admin/companies');
      setCompanies(res.data.companies || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleSaved = (updated) => {
    setCompanies(prev => prev.map(c => c.id === updated.id ? updated : c));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Company Module & Connector Whitelists</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchCompanies}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Empty whitelist = company users see whatever they're individually granted.
        Non-empty modules whitelist = users in the company can only ever see those modules,
        intersected with their per-user permissions. Non-empty connectors whitelist = the
        "Add New Connector" screen only shows those platforms.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Enabled Modules</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Enabled Connectors</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map(c => {
                const platforms = Array.isArray(c.enabled_platforms) ? c.enabled_platforms : [];
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.slug}</TableCell>
                    <TableCell>{c.subscription_status}</TableCell>
                    <TableCell>
                      {c.enabled_modules.length === 0
                        ? <Chip label="No restriction" size="small" color="default" />
                        : <Typography variant="caption">{c.enabled_modules.length} module(s): {c.enabled_modules.join(', ')}</Typography>}
                    </TableCell>
                    <TableCell>
                      {platforms.length === 0
                        ? <Chip label="No restriction" size="small" color="default" />
                        : <Typography variant="caption">{platforms.length} connector(s): {platforms.join(', ')}</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setEditing(c)}
                        sx={{ mr: 1 }}
                      >
                        Modules
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CableIcon />}
                        onClick={() => setEditingConnectors(c)}
                      >
                        Connectors
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CompanyModulesDialog
        open={!!editing}
        company={editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />
      <CompanyConnectorsDialog
        open={!!editingConnectors}
        company={editingConnectors}
        onClose={() => setEditingConnectors(null)}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default CompanyModulesTab;
